import { useState, useEffect, useContext, useMemo } from 'react';
import _ from 'underscore';
import _preferencesService from '@bingads-webui-component-legacy/preference/service/index';
import { useWizardService } from '@bingads-webui-react/wizard';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { isAudienceCampaign as isAudienceCampaignFlow, isMultiAdsAudienceCampaign, isCTVCampaign, isDealCampaign } from '../../step-constants';

const BiddingSchemeToBidType = {
  EnhancedCpc: 'SearchBid',
  ManualCpc: 'SearchBid',
  ManualCpm: 'CpmBid',
  ManualCpv: 'CpvBid',
};

const buildSchemaData = ({
  data, timeDisplayType, isAudienceCampaign, serverContext, numberOfAds, isCTVFlow, isDealFlow,
}) => {
  const {
    DynamicPermissions: {
      GenreTargets,
      CampaignLifetimeBudget,
      MaeLifetimeBudgetExclusion,
    },
  } = serverContext;

  const isCampaignLifetimeBudgetEnabled = CampaignLifetimeBudget && !MaeLifetimeBudgetExclusion;

  const locations = _.map(
    _.filter(
      data.adGroup.locationTargets.currentLevelSettings.currentLocations,
      loc => loc.SelectionStatus === 'Included'
    ),
    loc => loc.location
  );

  const { deal, campaign = {}, adGroup = {} } = data;
  const hideGenreTargetForDeal = isDealFlow ? !(deal && deal.Genre) : false;

  const isUseCampaignLevelDates = campaign.UseCampaignLevelDates;
  const startDate = isUseCampaignLevelDates ? campaign.StartDate : adGroup.StartDate;
  const endDate = isUseCampaignLevelDates ? campaign.EndDate : adGroup.EndDate;

  return ({
    adsPanel: {
      isRendered: !_.isUndefined(data.ads),
    },
    adCreation: {
      numAds: numberOfAds,
    },
    adGroupName: {
      data: data.adGroup.Name,
    },
    locationTargeting: {
      locations,
      targetOption: data.adGroup.locationTargets.currentLevelSettings.targetOption,
    },
    genderTargeting: {
      genders: data.adGroup.genderTypes,
      isRendered: data.adGroup.genderTypes && data.adGroup.genderTypes.length > 0,
    },
    ageRangeTargeting: {
      ageRanges: data.adGroup.ageRanges,
      isRendered: data.adGroup.ageRanges && data.adGroup.ageRanges.length > 0,
    },
    genreTargeting: {
      genreTargets: data.adGroup.genreTargets,
      isRendered: GenreTargets && (isCTVFlow || (isDealFlow && !hideGenreTargetForDeal)),
    },
    predictiveTargeting: {
      isPredictiveTargetingEnabled: data.adGroup.UseOptimizedTargeting,
      isRendered: isAudienceCampaign,
    },
    advancedTargetingSeparator: {},
    interest: {
      interests: data.adGroup.interests,
      isRendered: data.adGroup.interests && data.adGroup.interests.length > 0,
    },
    audience: {
      audiences: data.adGroup.audiences,
      isRendered: data.adGroup.audiences && data.adGroup.audiences.length > 0,
    },
    company: {
      companyTargets: data.adGroup.companyNameTargets,
      negativeCompanyTargets: data.adGroup.negativeCompanyNameTargets,
      isRendered: (data.adGroup.companyNameTargets && data.adGroup.companyNameTargets.length > 0) || (data.adGroup.negativeCompanyNameTargets && data.adGroup.negativeCompanyNameTargets.length > 0),
    },
    jobFunction: {
      jobTargets: data.adGroup.jobTargets,
      negativeJobTargets: data.adGroup.negativeJobTargets,
      isRendered: (data.adGroup.jobTargets && data.adGroup.jobTargets.length > 0) || (data.adGroup.negativeJobTargets && data.adGroup.negativeJobTargets.length > 0),
    },
    industry: {
      industryTargets: data.adGroup.industry,
      negativeIndustryTargets: data.adGroup.negativeIndustry,
      isRendered: (data.adGroup.industry && data.adGroup.industry.length > 0) || (data.adGroup.negativeIndustry && data.adGroup.negativeIndustry.length > 0),
    },
    adScheduleAdGroup: {
      startDate: data.adGroup.StartDate,
      endDate: data.adGroup.EndDate,
      adSchedules: data.adGroup.adSchedules.dayTimeRanges,
      useSearcherTimeZone: data.adGroup.AdScheduleUseSearcherTimeZone,
      timeDisplayType,
      isRendered: !isAudienceCampaign,
    },
    campaignName: {
      data: data.campaign.Name,
    },
    conversionGoals: {
      conversionGoals: data.campaign.conversionGoals,
      isRendered: !(isCTVFlow || isDealFlow),
    },
    budget: {
      data: data.campaign.Budget,
      currencyCode: serverContext.CurrentAccount.CurrencyCode,
    },
    bidStrategy: {
      data: data.campaign.BiddingScheme,
    },
    bidValue: {
      data: data.adGroup[BiddingSchemeToBidType[data.campaign.BiddingScheme.Type]],
      currencyCode: serverContext.CurrentAccount.CurrencyCode,
      isRendered: data.campaign.BiddingScheme && data.campaign.BiddingScheme.Type !== 'MaxConversions',
    },
    startEndDatesCampaign: {
      startDate,
      endDate,
      isUseCampaignLevelDates,
      isDateLevelDisplayed: isAudienceCampaign && isCampaignLifetimeBudgetEnabled,
      isRendered: isAudienceCampaign,
    },
  });
};

export const useSummarySchemaData = ({ data }) => {
  const { state } = useWizardService();
  const { flow } = state.context.wizardData;
  const isAudienceCampaign = isAudienceCampaignFlow(flow);
  const isCTVFlow = isCTVCampaign(flow);
  const isDealFlow = isDealCampaign(flow);

  const [timeDisplayType, setTimeDisplayType] = useState('12-hour');

  const serverContext = useContext(ServerContext);

  const numberOfAds = useMemo(() => {
    if (isMultiAdsAudienceCampaign(flow)) {
      return data && data.ads && data.ads.ads && data.ads.ads && Object.keys(data.ads.ads).length;
    }

    return (data && data.ads && data.ads.ads && data.ads.ads.data && data.ads.ads.data.length) || 1;
  }, [data, flow]);

  useEffect(() => {
    const preferencesService = _preferencesService.default || _preferencesService;
    preferencesService.initialize().then(() => {
      const isTwelveHourDisplay = preferencesService.findByName('WebApp.CampaignUI.isTwelveHourDisplay') || {};
      setTimeDisplayType(_.isUndefined(isTwelveHourDisplay.Data) || isTwelveHourDisplay.Data ? '12-hour' : '24-hour');
    });
  }, []);

  return (data && buildSchemaData({
    data, timeDisplayType, isAudienceCampaign, serverContext, numberOfAds, isCTVFlow, isDealFlow,
  })) || {};
};

